import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Button, Card, CardBody, CardHeader, CardFooter } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import getChartColorsArray from '../../../../../Components/Common/ChartsDynamicColor';
import { getHourMeterSummary, getProductiveCycleActive } from '../../../../../slices/thunks';

import moment from 'moment';
import 'moment/locale/es'; // Importar el idioma español
import BarChart from '../../../../../Components/Common/Charts/BarChart';
import { generateColorGradient } from '../../../../../Components/Hooks/useColorArrayHooks';
moment.locale('es'); // Configurar el idioma


const prepareChartData = (data, colorsChart, filter) => {
    if (!Array.isArray(data) || data.length === 0) {
        //console.warn("prepareChartData: Recibió data vacía o incorrecta.");
        return { series: [], categories: [], colors: [] };
    }

    // Definir formato de fecha basado en el filtro seleccionado
    const dateFormat = filter === "1M"
        ? "YYYY-[S]WW"
        : filter === "1Y"
            ? "YYYY"
            : ["3M", "6M", "AC"].includes(filter)
                ? "MM-YYYY"
                : "DD-MM-YYYY";

    // Agrupar datos solo por fecha
    const groupedData = data.reduce((acc, item) => {
        if (!item.register_date) {
            //console.warn("prepareChartData: Elemento con datos incompletos.", item);
            return acc;
        }

        const date = moment(item.register_date).format(dateFormat);
        const hours = Number(item.hours) || 0;

        if (!acc[date]) {
            acc[date] = { date, hours: 0 };
        }
        acc[date].hours += hours;

        return acc;
    }, {});

    // Convertir el objeto agrupado en un array ordenado por fecha
    const sortedData = Object.values(groupedData).sort((a, b) =>
        moment(a.date, dateFormat).diff(moment(b.date, dateFormat))
    );

    if (sortedData.length === 0) {
        //console.warn("prepareChartData: No se encontraron datos agrupados.");
        return { series: [], categories: [], colors: [] };
    }

    // Obtener categorías (fechas únicas)
    const categories = sortedData.map(item => item.date);

    // Construcción de la serie única
    const series = [
        {
            name: "Total Horas",
            data: categories.map(date => {
                const record = sortedData.find(item => item.date === date);
                return record ? { x: date, y: record.hours } : { x: date, y: 0 };
            })
        }
    ];

    return {
        series,
        categories,
        colors: [colorsChart[0]] // Usar solo un color para la serie única
    };
};





const getUniqueUnits = (data) => {
    if (!Array.isArray(data) || data.length === 0) return "N/A";

    const uniqueUnits = new Set(
        data
            .map(item => item.unit_measurement_info?.name?.trim()) // Acceder correctamente a `unit_measurement_info`
            .filter(Boolean) // Elimina valores vacíos, null o undefined
            .map(unit => unit.charAt(0).toUpperCase() + unit.slice(1)) // Capitalizar solo si hay datos
    );

    return uniqueUnits.size > 0 ? Array.from(uniqueUnits).join(", ") : "N/A";
};


const tooltipFormatter = (data) => {
    const extra = data?.series?.extra || {};
    if (!extra) return "";

    const { barge, stock, capacity, unit, tankName, fuelType } = extra;

    return `
        <div style="
            border-radius: 8px;
            padding: 10px;
            font-size: 11px; 
            text-align: left;
            max-width: 200px;">
            <div>Nombre: <span style="font-weight: bold;">${tankName}</span></div>
            ${barge !== "No aplica" ? `<div>Artefacto Naval: <span style="font-weight: bold;">${barge}</span></div>` : ""}
            <div>Capacidad: <span style="font-weight: bold;">${capacity.toLocaleString()} ${unit}</span></div>
            <div>Saldo: <span style="font-weight: bold;">${stock.toLocaleString()} ${unit}</span></div>
            <div>Tipo Combustible: <span style="font-weight: bold;">${fuelType}</span></div>
        </div>`;
};


const HourMeterBalance = ({ establishment, title, option, um = 'Horas', minHeight, maxHeight }) => {
    const dispatch = useDispatch();
    const [selectedFilter, setSelectedFilter] = useState(option);
    const [chartData, setChartData] = useState({ series: [], categories: [], fuelSummary: [] });

    const selectorFuelSupply = createSelector(
        state => state.FuelSupply,
        (_, type) => type,
        (data, type) => ({
            info: data.hourmeterSummary[type] || null, // Acceder a los datos correctamente
            loading: data.isLoadingList,
        })
    );

    // Obtener datos en el componente
    const { info, loading } = useSelector(state => selectorFuelSupply(state, 'balance'));

    useEffect(() => {
        if (establishment?._id && selectedFilter) {
            dispatch(getHourMeterSummary({
                establishment_id: establishment._id,
                filter: selectedFilter,
                type: 'balance',
            }));
        }
    }, [establishment, selectedFilter]); // Asegurar dependencias correctas



    const selectedFilterRef = useRef(selectedFilter); // Mantiene el valor anterior del filtro

    const handleFilterChange = (filter) => {
        console.log(1)
        if (selectedFilterRef.current === filter) return; // Evita llamadas duplicadas

        setSelectedFilter(filter);
        selectedFilterRef.current = filter; // Actualiza el ref inmediatamente

        if (establishment?._id) {
            dispatch(getHourMeterSummary({
                establishment_id: establishment._id,
                filter, // Usa el nuevo valor directamente
                type: 'balance',
            }));
        }
    };


    const yAxisUnits = getUniqueUnits(info?.data);
    const colorsChart = useMemo(() => generateColorGradient(chartData.series.length ?? 0))

    useEffect(() => {
        if (Array.isArray(info?.data) && info?.data.length > 0 && colorsChart.length > 0) {
            setChartData(prepareChartData(info?.data, colorsChart, selectedFilter));
        } else {
            console.warn("useEffect: Data está vacía o colorsChart no está definido.");
        }
    }, [info?.data, colorsChart, selectedFilter]);


    if (info?.data?.length == 0) {
        return (
            <Card className='h-100 p-0 mb-2'>
                <CardHeader className='p-2 align-items-center d-flex'>
                    <h5 className="flex-grow-1 m-0">{title}</h5>
                </CardHeader>
                <CardBody className='p-2'>
                    <div className="d-flex justify-content-center align-items-center p-3">
                        <div className="text-center">
                            <div className="avatar-md">
                                <span className="avatar-title bg-info-subtle rounded-circle fs-1">
                                    <i className="ri-timer-2-fill text-info"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                </CardBody>
                <CardFooter className="m-0 p-2 text-center">
                    <h6 className="mb-0 p-0 text-wrap w-100">
                        <span className="fs-11 badge bg-info-subtle text-info text-wrap">
                            No cuenta con registros de horómetro
                        </span>
                    </h6>
                </CardFooter>
            </Card>
        );
    }


    return (
        <React.Fragment>
            <Card className='m-0 h-100'>
                <CardHeader className='p-2 align-items-center d-flex'>
                    <h5 className="card-title mb-0 flex-grow-1">{title}</h5>
                    <div className='btn-box-dashboard'>
                        {/* <Button
                            color={selectedFilter === 'AC' ? "primary" : "secondary"}
                            size="sm"
                            className={selectedFilter === 'AC' ? "btn-soft-primary me-1" : "btn-soft-secondary me-1"}
                            onClick={() => handleFilterChange('AC')}
                            title="Ciclo Activo"
                        >
                            CA
                        </Button> */}
                        <Button
                            color={selectedFilter === '1Y' ? "primary" : "secondary"}
                            size="sm"
                            className={selectedFilter === '1Y' ? "btn-soft-primary me-1" : "btn-soft-secondary me-1"}
                            onClick={() => handleFilterChange('1Y')}
                            title="1 Año"
                        >
                            1A
                        </Button>
                        <Button
                            color={selectedFilter === '6M' ? "primary" : "secondary"}
                            size="sm"
                            className={selectedFilter === '6M' ? "btn-soft-primary me-1" : "btn-soft-secondary me-1"}
                            onClick={() => handleFilterChange('6M')}
                            title="6 Meses"
                        >
                            6M
                        </Button>
                        <Button
                            color={selectedFilter === '3M' ? "primary" : "secondary"}
                            size="sm"
                            className={selectedFilter === '3M' ? "btn-soft-primary me-1" : "btn-soft-secondary me-1"}
                            onClick={() => handleFilterChange('3M')}
                            title="3 Meses"
                        >
                            3M
                        </Button>
                        <Button
                            color={selectedFilter === '1M' ? "primary" : "secondary"}
                            size="sm"
                            className={selectedFilter === '1M' ? "btn-soft-primary" : "btn-soft-secondary"}
                            onClick={() => handleFilterChange('1M')}
                            title="1 Mes"
                        >
                            1M
                        </Button>
                    </div>
                </CardHeader>


                {/* Gráfico de stock de combustible */}
                <CardBody className="p-0">
                    <BarChart
                        seriesChart={chartData.series}
                        categoriesChart={chartData.categories}
                        colorsChart={chartData.colors}
                        toolbarChart={true}
                        height={'100%'}
                        minHeight={minHeight}
                        maxHeight={maxHeight}
                        legendPosition={'bottom'}
                        legendOffsetY={2}
                        strokeCurve={'smooth'}
                        chartType={'bar'}
                        horizontal={false}
                        yAxisTitle={'Horas'}
                        um={um}
                        tooltipFormatter={tooltipFormatter}
                    />
                </CardBody>
            </Card>
        </React.Fragment>
    );
};

export default HourMeterBalance;
